import React, { useState } from 'react';
import { Input, DatePicker, Modal, Checkbox, message } from 'antd';
import { Link } from 'react-router-dom';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
    Search,
    Filter,
    CalendarDays,
    CheckCircle2,
    UserPlus,
    PenTool,
    Mail,
    PenLine,
    ChevronRight,
    FileCheck,
    Building2,
    Briefcase,
    Users,
    X,
    Plus,
    Check,
} from 'lucide-react';
import Layout from '../../Layout'

const { TextArea } = Input;

// --- MOCK DATA ---
const templates = [
    { id: 1, title: 'Xin nghỉ phép (nhiều ngày)', desc: 'Quy trình xin nghỉ phép dành cho CBNV THACO AUTO' },
    { id: 2, title: 'Xin nghỉ phép (1 ngày)', desc: 'Quy trình xin nghỉ phép dành cho CBNV THACO AUTO' },
    { id: 3, title: 'Hợp đồng tuyển dụng', desc: 'Quy trình tuyển dụng nhân viên' },
    { id: 4, title: 'Quy trình đưa nhân viên lên chính thức', desc: 'Quy trình đưa nhân viên cho TTV THACO AUTO' },
];

const mockCCUsers = [
    { id: 1, name: 'Phan Duy Anh', email: 'duy.phambbsw@gmail.com', avatar: 'https://i.pravatar.cc/150?u=10' },
    { id: 2, name: 'Phan Duy Anh (Clone)', email: 'duy.phambbsw@gmail.com', avatar: 'https://i.pravatar.cc/150?u=11' },
    { id: 3, name: 'Nguyễn Văn B', email: 'nguyenvanb@gmail.com', avatar: 'https://i.pravatar.cc/150?u=12' },
    { id: 4, name: 'Trần Thị C', email: 'tranthic@gmail.com', avatar: 'https://i.pravatar.cc/150?u=13' },
    { id: 5, name: 'Lê Hoàng D', email: 'lehoangd@gmail.com', avatar: 'https://i.pravatar.cc/150?u=14' },
];

const approvers = [
    { id: 1, stepName: 'Xem xét', user: 'Phạm Anh Duy', role: 'Trưởng phòng', time: '1 giờ', avatar: 'https://i.pravatar.cc/150?u=4' },
    { id: 2, stepName: 'Duyệt', user: 'Huỳnh Thị Mỹ Hồng', role: 'Giám đốc', time: '1 giờ', avatar: 'https://i.pravatar.cc/150?u=5' },
    { id: 3, stepName: 'Ký chính', user: 'Lê Hoàng Đạt', role: 'Chủ tịch', time: '1 giờ', avatar: 'https://i.pravatar.cc/150?u=7' },
];

export default function CreateTemplateForm() {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    // Quản lý Đóng/Mở Modal
    const [isCCModalOpen, setIsCCModalOpen] = useState(false);
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);

    // Quản lý Dữ liệu người dùng đã chọn
    const [selectedCCUsers, setSelectedCCUsers] = useState([]); // Chứa user hiển thị ở màn hình chính
    const [tempSelectedUsers, setTempSelectedUsers] = useState([]); // Chứa user khi đang thao tác trong Modal 2

    // --- LOGIC XỬ LÝ CHO MODAL 2 ---
    // Chọn / Bỏ chọn 1 user
    const handleToggleUser = (user) => {
        const isSelected = tempSelectedUsers.some(u => u.id === user.id);
        if (isSelected) {
            setTempSelectedUsers(tempSelectedUsers.filter(u => u.id !== user.id));
        } else {
            setTempSelectedUsers([...tempSelectedUsers, user]);
        }
    };

    // Chọn / Bỏ chọn tất cả
    const handleSelectAll = (e) => {
        if (e.target.checked) setTempSelectedUsers([...mockCCUsers]);
        else setTempSelectedUsers([]);
    };

    // Áp dụng danh sách vào màn hình chính
    const handleConfirmUsers = () => {
        setSelectedCCUsers(tempSelectedUsers);
        setIsUserModalOpen(false);
    };

    // Xử lý khi chọn một mẫu ở Bước 1
    const handleSelectTemplate = (template) => {
        setSelectedTemplate(template);
        setCurrentStep(2); // Chuyển sang bước 2
    };

    // Hàm xử lý khi nhấn nút Trình ký
    const handleSubmit = () => {
        // 1. Hiển thị thông báo thành công
        message.success('Thiết lập thành công!');

        // 2. Đợi 1 giây (1000ms) rồi mới chuyển trang về /request
        setTimeout(() => {
            navigate('/request');
        }, 1000);
    };

    return (
        <Layout>
            <div className="flex items-center gap-2 text-sm">
                <Link to="/request" className="flex items-center gap-2">
                    <LeftOutlined className="w-6 h-6 flex items-center justify-center rounded-full bg-[#3e91ab] hover:bg-[#357f96] text-white" />
                </Link>
                <Link to="/request" className="flex items-center gap-2 text-000000e0">
                    <span>Yêu cầu</span>
                </Link>
                <span><RightOutlined className="w-4 h-4 text-[12px] text-gray-500 text-000000e0" /></span>
                <span className="text-000000e0">Tạo mới trình duyệt</span>
            </div>

            <div className="max-w-5xl mx-auto pt-6">

                {/* =======================
            BƯỚC 1: CHỌN QUY TRÌNH MẪU 
            ======================= */}
                {currentStep === 1 && (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">

                        {/* Stepper Step 1 (Responsive) */}
                        <div className="flex items-start md:items-center justify-between md:justify-center mb-8 md:mb-10 w-full max-w-2xl mx-auto gap-2 md:gap-0">

                            {/* Step 1: Active */}
                            <div className="flex flex-col md:flex-row items-center md:items-start gap-2 md:gap-3 text-center md:text-left w-[45%] md:w-auto">
                                <div className="shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-full bg-blue-600 text-white flex items-center justify-center font-medium shadow-sm text-sm md:text-base">
                                    1
                                </div>
                                <div className="flex flex-col justify-center">
                                    <div className="text-[13px] md:text-[15px] font-medium text-gray-800 dark:text-gray-200 leading-tight">
                                        Chọn quy trình mẫu
                                    </div>
                                    <div className="text-[11px] md:text-[13px] text-gray-500 dark:text-gray-400 mt-1 leading-tight hidden sm:block">
                                        Chọn quy trình để thực hiện trình duyệt
                                    </div>
                                </div>
                            </div>

                            {/* Đường kẻ nối */}
                            <div className="flex-1 mt-4 md:mt-0 max-w-[120px] h-[1px] bg-gray-200 dark:bg-gray-700 mx-2 md:mx-6 lg:mx-8"></div>

                            {/* Step 2: Pending/Inactive */}
                            <div className="flex flex-col md:flex-row items-center md:items-start gap-2 md:gap-3 opacity-60 text-center md:text-left w-[45%] md:w-auto">
                                <div className="shrink-0 w-8 h-8 md:w-6 flex items-center justify-center text-[14px] md:text-[16px] font-medium text-gray-500">
                                    2
                                </div>
                                <div className="flex flex-col justify-center">
                                    <div className="text-[13px] md:text-[15px] font-medium text-gray-500 dark:text-gray-400 leading-tight">
                                        Thông tin trình ký
                                    </div>
                                    <div className="text-[11px] md:text-[13px] text-gray-400 dark:text-gray-500 mt-1 leading-tight hidden sm:block">
                                        Bổ sung thông tin
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Content Step 1 */}
                        <div className="max-w-2xl mx-auto bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-4 md:p-6">
                            <div className="flex items-center gap-2 mb-4 md:mb-6">
                                <Input
                                    prefix={<Search className="w-4 h-4 text-gray-400" />}
                                    placeholder="Tìm kiếm"
                                    className="w-full dark:bg-gray-800 dark:border-gray-700 dark:text-white rounded-md h-10"
                                />
                                <button className="p-2 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 shrink-0">
                                    <Filter className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>

                            <div className="space-y-3">
                                {templates.map((tpl) => (
                                    <div
                                        key={tpl.id}
                                        onClick={() => handleSelectTemplate(tpl)}
                                        className="flex items-start gap-3 md:gap-4 p-3 md:p-4 rounded-lg border border-gray-100 dark:border-gray-800 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-md cursor-pointer transition-all bg-white dark:bg-gray-800/50 group"
                                    >
                                        <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center shrink-0">
                                            <CalendarDays className="w-4 h-4 md:w-5 md:h-5 text-red-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm md:text-base font-medium text-gray-800 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                                {tpl.title}
                                            </h3>
                                            <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{tpl.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* =======================
            BƯỚC 2: KHAI BÁO THÔNG TIN 
            ======================= */}
                {currentStep === 2 && (
                    <div className="animate-in fade-in slide-in-from-right-8 duration-500">

                        {/* Stepper Step 2 (Detailed - Horizontal scroll on mobile) */}
                        <div className="w-full overflow-x-auto hide-scrollbar mb-8 md:mb-14 pb-2 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0">
                            <div className="flex w-full min-w-[680px] md:min-w-[760px] max-w-5xl mx-auto py-2">
                                {[
                                    { id: 1, title: 'Chọn quy trình mẫu', sub: 'Chọn quy trình để thực hiện trình duyệt', icon: FileCheck, status: 'done' },
                                    { id: 2, title: 'Khai báo', sub: '-', icon: UserPlus, status: 'active' },
                                    { id: 3, title: 'Thiết lập vị trí ký', sub: '-', icon: PenTool, status: 'pending' },
                                    { id: 4, title: 'Thiết lập trước khi gửi', sub: '-', icon: Mail, status: 'pending' },
                                ].map((step, index, arr) => {

                                    const isLast = index === arr.length - 1;
                                    const isDone = step.status === 'done';
                                    const isActive = step.status === 'active';

                                    const iconColor = (isDone || isActive) ? 'text-blue-500 dark:text-blue-400' : 'text-gray-300 dark:text-gray-600';
                                    const circleBg = isDone ? 'bg-green-500' : isActive ? 'bg-blue-500' : 'bg-transparent border-2 border-gray-200 dark:border-gray-700';
                                    const circleText = (isDone || isActive) ? 'text-white' : 'text-gray-400 dark:text-gray-500';
                                    const titleColor = isDone ? 'text-gray-500 dark:text-gray-400' : isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500';
                                    const lineColor = isDone ? 'bg-blue-400/60 dark:bg-blue-800' : 'bg-gray-200 dark:bg-gray-800';

                                    return (
                                        <div key={step.id} className={`flex ${isLast ? 'flex-none' : 'flex-1 overflow-hidden'}`}>
                                            <div
                                                className="flex flex-col relative pr-6 md:pr-8 w-fit cursor-pointer group"
                                                onClick={() => { if (isDone) setCurrentStep(step.id) }}
                                            >
                                                <div className="w-full flex justify-center mb-3 md:mb-4 transition-transform group-hover:scale-105">
                                                    <step.icon className={`w-10 h-10 md:w-12 md:h-12 ${iconColor}`} strokeWidth={1.2} />
                                                </div>

                                                <div className="flex items-start gap-2 md:gap-3">
                                                    <div className={`shrink-0 w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center text-xs md:text-sm font-medium transition-colors ${circleBg} ${circleText}`}>
                                                        {isDone ? <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} /> : step.id}
                                                    </div>

                                                    <div className="flex flex-col pt-0.5">
                                                        <div className="relative w-fit">
                                                            <span className={`text-[14px] md:text-[15px] font-medium whitespace-nowrap ${titleColor}`}>
                                                                {step.title}
                                                            </span>
                                                            {!isLast && (
                                                                <div className="absolute top-1/2 left-full ml-3 md:ml-4 w-[9999px] h-[2px] -translate-y-1/2">
                                                                    <div className={`w-full h-full transition-colors duration-300 ${lineColor}`}></div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <span className="text-[12px] md:text-[13px] text-gray-400 dark:text-gray-500 mt-1 max-w-[140px] leading-relaxed hidden md:block">
                                                            {step.sub}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Form Content Step 2 */}
                        <div className="max-w-3xl mx-auto bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden">
                            <div className="p-4 md:p-6 lg:p-8">
                                <h2 className="text-base md:text-lg font-bold text-gray-800 dark:text-gray-100 mb-4 md:mb-6 border-b border-gray-100 dark:border-gray-800 pb-3 md:pb-4">
                                    {selectedTemplate?.title || 'Xin nghỉ phép (nhiều ngày)'}
                                </h2>

                                <div className="space-y-5 md:space-y-6">
                                    {/* Row: Date Pickers */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                                                Ngày bắt đầu <span className="text-red-500">*</span>
                                            </label>
                                            <DatePicker className="w-full h-10 dark:bg-gray-800 dark:border-gray-700" placeholder="Chọn ngày bắt đầu" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                                                Ngày kết thúc <span className="text-red-500">*</span>
                                            </label>
                                            <DatePicker className="w-full h-10 dark:bg-gray-800 dark:border-gray-700" placeholder="Chọn ngày kết thúc" />
                                        </div>
                                    </div>

                                    {/* Row: Reason */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                                            Lý do <span className="text-red-500">*</span>
                                        </label>
                                        <TextArea
                                            rows={4}
                                            placeholder="Nhập lý do xin nghỉ phép"
                                            maxLength={1000}
                                            showCount
                                            className="dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                                        />
                                    </div>

                                    {/* Workflow Steps Section */}
                                    <div className="mt-6 md:mt-8">
                                        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 md:mb-4">
                                            Các bước thực hiện <span className="text-red-500">*</span>
                                        </label>

                                        <div className="space-y-3 md:space-y-4">
                                            {approvers.map((step, index) => (
                                                <div key={step.id} className="flex gap-3 md:gap-4">
                                                    <div className="mt-1 shrink-0">
                                                        <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-blue-600 text-white text-[10px] md:text-xs flex items-center justify-center">
                                                            {index + 1}
                                                        </div>
                                                    </div>
                                                    <div className="flex-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex flex-col xl:flex-row xl:items-center justify-between gap-3 shadow-sm">
                                                        <div className="text-sm font-medium text-gray-700 dark:text-gray-200">{step.stepName}</div>

                                                        {/* Thông tin người duyệt linh hoạt tự bẻ dòng trên mobile */}
                                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                                            <div className="flex items-center gap-2">
                                                                <img src={step.avatar} alt={step.user} className="w-5 h-5 md:w-6 md:h-6 rounded-full" />
                                                                <span className="text-xs md:text-sm font-medium dark:text-gray-300">{step.user}</span>
                                                            </div>
                                                            <span className="text-gray-300 dark:text-gray-600 hidden sm:block">|</span>
                                                            <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400">{step.role}</span>
                                                            <span className="text-gray-300 dark:text-gray-600 hidden sm:block">|</span>
                                                            <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400">{step.time}</span>
                                                        </div>

                                                        <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 text-xs md:text-sm font-medium cursor-pointer hover:underline self-start xl:self-auto">
                                                            <span>Nội bộ</span>
                                                            <PenLine className="w-3.5 h-3.5" />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Auto CC */}
                                    <div className="mt-5 md:mt-6">
                                        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 md:mb-3">
                                            CC Tự động
                                        </label>

                                        {/* Render điều kiện: Nếu chưa chọn ai thì hiện Input giả, nếu đã chọn thì hiện Avatar */}
                                        {selectedCCUsers.length === 0 ? (
                                            <div
                                                onClick={() => setIsCCModalOpen(true)}
                                                className="flex items-center justify-between border border-gray-200 dark:border-gray-700 rounded-lg p-2.5 px-3 md:px-4 cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 bg-white dark:bg-gray-800 transition-colors"
                                            >
                                                <span className="text-xs md:text-sm text-gray-400">Thêm tài khoản CC tự động</span>
                                                <ChevronRight className="w-4 h-4 text-gray-400" />
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-4 py-1">
                                                {/* Cụm Avatar xếp chồng lên nhau */}
                                                <div className="flex -space-x-3">
                                                    {selectedCCUsers.map((user) => (
                                                        <img
                                                            key={user.id}
                                                            src={user.avatar}
                                                            alt={user.name}
                                                            title={user.name}
                                                            className="w-9 h-9 rounded-full border-2 border-white dark:border-gray-900 object-cover shadow-sm relative z-10 hover:z-20 transition-all cursor-pointer"
                                                        />
                                                    ))}
                                                </div>

                                                {/* Nút Thêm tài khoản dạng Pill */}
                                                <button
                                                    onClick={() => {
                                                        setTempSelectedUsers(selectedCCUsers); // Đồng bộ lại modal với danh sách cũ
                                                        setIsCCModalOpen(true);
                                                    }}
                                                    className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-full text-sm font-medium transition-colors shadow-sm"
                                                >
                                                    <Plus className="w-4 h-4" />
                                                    Thêm tài khoản
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Footer Buttons */}
                            <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 p-4 flex justify-end gap-2 md:gap-3">
                                <button
                                    onClick={() => setCurrentStep(1)}
                                    className="px-4 md:px-6 py-2 rounded-md text-sm md:text-base text-gray-600 dark:text-gray-300 font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                >
                                    Hủy
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    className="px-4 md:px-6 py-2 rounded-md text-sm md:text-base bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors shadow-sm"
                                >
                                    Trình ký
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>

            {/* =====================================
          MODAL 1: CHỌN LOẠI CC (Đơn vị / Người dùng)
          ===================================== */}
            <Modal
                title={<div className="text-base font-bold text-gray-800">CC Tự động</div>}
                open={isCCModalOpen}
                onCancel={() => setIsCCModalOpen(false)}
                footer={[
                    <button
                        key="cancel"
                        onClick={() => setIsCCModalOpen(false)}
                        className="px-6 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md mr-3 transition-colors"
                    >
                        Hủy
                    </button>,
                    <button
                        key="apply"
                        onClick={() => setIsCCModalOpen(false)}
                        className="px-6 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                        Áp dụng
                    </button>
                ]}
                width={500}
                centered
            >
                <div className="flex flex-col gap-1 py-4">
                    <div className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer rounded-lg transition-colors">
                        <div className="flex items-center gap-3 text-blue-600 font-medium">
                            <Building2 className="w-5 h-5" /> Đơn vị
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>

                    <div className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer rounded-lg transition-colors">
                        <div className="flex items-center gap-3 text-blue-600 font-medium">
                            <Briefcase className="w-5 h-5" /> Chức danh công việc
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>

                    <div
                        onClick={() => {
                            setTempSelectedUsers(selectedCCUsers); // Khởi tạo temp list bằng những user đã chọn từ trước
                            setIsUserModalOpen(true);
                            setIsCCModalOpen(false); // Ẩn Modal 1
                        }}
                        className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer rounded-lg transition-colors"
                    >
                        <div className="flex items-center gap-3 text-blue-600 font-medium">
                            <Users className="w-5 h-5" /> Người dùng
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                </div>
            </Modal>

            {/* =====================================
          MODAL 2: PICKER CHỌN NGƯỜI DÙNG
          ===================================== */}
            <Modal
                title={<div className="text-base font-bold text-gray-800 border-b border-gray-100 pb-3">Người dùng</div>}
                open={isUserModalOpen}
                onCancel={() => setIsUserModalOpen(false)}
                width={750}
                centered
                closeIcon={<X className="w-5 h-5 text-gray-400 mt-1" />}
                footer={null} // Tắt footer mặc định để custom hoàn toàn giống hình
                className="user-picker-modal"
            >
                <div className="flex h-[450px]">

                    {/* CỘT TRÁI: Tìm kiếm & Danh sách chọn */}
                    <div className="w-1/2 border-r border-gray-200 pr-5 flex flex-col pt-4">
                        <div className="flex items-center gap-3 mb-4">
                            <Input
                                prefix={<Search className="w-4 h-4 text-gray-400" />}
                                placeholder="Tìm kiếm..."
                                className="flex-1 rounded-md"
                            />
                            <Checkbox
                                checked={tempSelectedUsers.length === mockCCUsers.length && mockCCUsers.length > 0}
                                onChange={handleSelectAll}
                                className="text-sm text-gray-600 whitespace-nowrap"
                            >
                                Chọn tất cả
                            </Checkbox>
                        </div>

                        <div className="flex-1 overflow-y-auto pr-2 space-y-1 hide-scrollbar">
                            {mockCCUsers.map(user => {
                                const isSelected = tempSelectedUsers.some(u => u.id === user.id);
                                return (
                                    <div
                                        key={user.id}
                                        onClick={() => handleToggleUser(user)}
                                        className="flex items-center justify-between py-2.5 border-b border-gray-100 cursor-pointer hover:bg-gray-50 px-2 rounded"
                                    >
                                        <div>
                                            <div className="text-sm font-medium text-gray-800">{user.name}</div>
                                            <div className="text-xs text-gray-500">{user.email}</div>
                                        </div>
                                        {/* Custom Radio-like Checkbox (Hình tròn) */}
                                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300 bg-white'}`}>
                                            {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    {/* CỘT PHẢI: Danh sách đã chọn & Footer */}
                    <div className="w-1/2 pl-5 flex flex-col pt-4 bg-gray-50/30">
                        <div className="flex justify-between items-center mb-4">
                            <span className="font-semibold text-sm text-gray-800">
                                Đã chọn ({tempSelectedUsers.length})
                            </span>
                            <button
                                onClick={() => setTempSelectedUsers([])}
                                className="text-xs text-white bg-blue-600 px-3 py-1.5 rounded hover:bg-blue-700 font-medium transition-colors"
                            >
                                Bỏ Chọn tất cả
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto pr-2 space-y-2 hide-scrollbar">
                            {tempSelectedUsers.map(user => (
                                <div key={user.id} className="flex items-center justify-between p-2.5 bg-white border border-gray-100 rounded shadow-sm">
                                    <div className="text-sm font-medium text-gray-700">{user.name}</div>
                                    <button
                                        onClick={() => handleToggleUser(user)}
                                        className="p-1 hover:bg-red-50 hover:text-red-500 text-gray-400 rounded transition-colors"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Custom Footer Nằm ngay trong cột phải để căn chỉnh */}
                        <div className="pt-4 flex justify-end">
                            <button
                                onClick={handleConfirmUsers}
                                className="px-8 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors"
                            >
                                Ok
                            </button>
                        </div>
                    </div>

                </div>
            </Modal>
        </Layout>
    );
}